import React, { useState, useEffect, useMemo } from "react";
import { Dropdown, Menu } from "antd";
import { Button } from "react-bootstrap";
import moment from "moment";
import { useSelector } from "react-redux";

import api from "../../../../services/api";
import { notify } from "../../../../services/toast.config";
import Table from "../../../../core/common/dataTable/index_without_search";
import Loader from "../../../../core/common/loader";
import TodoModal from "../../../../core/modals/todoModal";
import TransferTaskModal from "../../../../core/modals/transferTaskModal";
import TaskHistoryModal from "../../../../core/modals/taskHistoryModal";
import DeleteModal from "../../../../core/modals/deleteModal";
import { getFullName } from "../../../ers/reports-settings/report-type/helpers/form.handler";

import { useTodoData } from "../hooks/useTodoData";
import TodoToolbar from "./TodoToolbar";
import TodoFilterPanel from "./TodoFilterPanel";
import TodoStatsBar from "./TodoStatsBar";
import ExportTodoModal, { ExportPayload } from "./ExportTodoModal";
import {
  TAG_OPTIONS,
  STATUS_OPTIONS,
  RECURRING_OPTIONS,
  PRIORITY_OPTIONS,
  STAGE_OPTIONS,
  LIST_INITIAL_FILTERS,
} from "../constants";
import {
  formatTitle,
  capitalizeFirstLetter,
  getProgress,
  getStageButtonClass,
  getEmployeeImageUrl,
} from "../helpers";
import type { Todo, DateRangeFilter, ViewType } from "../types";

interface TodoListViewProps {
  view: ViewType;
}

const TodoListView: React.FC<TodoListViewProps> = ({ view }) => {
  const user = useSelector((state: any) => state.user.user);

  const {
    signature,
    allData,
    isLoading,
    selectedCompanyId,
    selectedCompanyType,
    selectedCompany,
    handleCompanySelect,
    refreshTodos,
    updateReadStatus,
    handleStageUpdate,
    handleDeleteTodo,
    handleTaskTransferred,
  } = useTodoData();

  const [searchText, setSearchText] = useState("");

  // Filtered results
  const [filteredTodos, setFilteredTodos] = useState<Todo[]>([]);
  const [filteredStatTodos, setFilteredStatTodos] = useState<Todo[]>([]);

  // Applied filter state
  const [selectedTag, setSelectedTag] = useState<string>("All Tags");
  const [selectedAssignee, setSelectedAssignee] = useState<string>("All");
  const [selectedFollower, setselectedFollower] = useState<string>("All");
  const [selectedStatus, setSelectedStatus] = useState<string>("0");
  const [selectedRecurring, setSelectedRecurring] = useState<string>("All");
  const [sortBy, setSortBy] = useState<string>("Last 7 Days");
  const [selectedReporter, setSelectedReporter] =
    useState<string>("All Reporters");
  const [dueDateFilter, setDueDateFilter] = useState<DateRangeFilter | null>(
    null,
  );
  const [createDateFilter, setcreateDateFilter] =
    useState<DateRangeFilter | null>(null);
  const [selectKey, setSelectKey] = useState(0);

  // Modal + panel state
  const [editModalTodo, setEditModalTodo] = useState<Todo | null>(null);
  const [viewModalTodo, setViewModalTodo] = useState<Todo | null>(null);
  const [deleteTodoId, setDeleteTodoId] = useState<string | null>(null);
  const [transferModalTodo, setTransferModalTodo] = useState<Todo | null>(null);
  const [logsModalTodo, setLogsModalTodo] = useState<Todo | null>(null);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState<boolean>(false);
  const [showExportModal, setShowExportModal] = useState(false);

  // Temp filter state used by the panel before "Apply".
  const [tempFilters, setTempFilters] = useState({ ...LIST_INITIAL_FILTERS });

  const activeFiltersCount = Object.entries(tempFilters).reduce(
    (count, [key, value]) => {
      if (key === "selectedTag" && value !== "All Tags") return count + 1;
      if (key === "selectedAssignee" && value !== "All") return count + 1;
      if (key === "selectedFollower" && value !== "All") return count + 1;
      if (key === "selectedStatus" && value !== "All") return count + 1;
      if (key === "selectedRecurring" && value !== "All") return count + 1;
      if (key === "dueDateFilter" && value) return count + 1;
      if (key === "createDateFilter" && value) return count + 1;
      if (key === "sortBy" && value !== "Last 7 Days") return count + 1;
      return count;
    },
    0,
  );

  // Sync temp filters when the panel opens.
  useEffect(() => {
    if (isFilterPanelOpen) {
      setTempFilters({
        selectedTag,
        selectedAssignee,
        selectedFollower,
        selectedReporter,
        selectedStatus,
        selectedRecurring,
        dueDateFilter,
        createDateFilter,
        sortBy,
      });
    }
  }, [
    isFilterPanelOpen,
    selectedTag,
    selectedAssignee,
    selectedFollower,
    selectedReporter,
    selectedStatus,
    selectedRecurring,
    dueDateFilter,
    createDateFilter,
    sortBy,
  ]);

  const [reporterOptions, setReporterOptions] = useState([
    { value: "All Reporters", label: "All Reporters" },
  ]);

  // Build the reporter dropdown from the current company's todos.
  useEffect(() => {
    if (allData.todos.length > 0 && selectedCompanyId && selectedCompanyType) {
      const companyTodos = allData.todos.filter(
        (todo) =>
          todo.company_id == selectedCompanyId &&
          todo.company_type == selectedCompanyType,
      );

      const uniqueReporters = companyTodos.reduce(
        (acc, todo) => {
          if (
            todo.reporter?.uid &&
            !acc.some((r) => r.value === todo.reporter?.uid.toString())
          ) {
            if (user?.userDetails.role_char.toLowerCase() === "admin") {
              acc.push({
                value: todo.reporter.uid.toString(),
                label: todo.reporter.name || "",
              });
            } else if (
              user?.userDetails.uid.toString() == todo.reporter?.uid.toString()
            ) {
              acc.push({
                value: todo.reporter.uid.toString(),
                label: todo.reporter.name || "",
              });
            }
          }
          return acc;
        },
        [] as { value: string; label: string }[],
      );

      setReporterOptions(uniqueReporters);
    }
  }, [allData.todos, selectedCompanyId, selectedCompanyType, user]);

  // Set initial company selection once companies load.
  useEffect(() => {
    if (allData.companies.length > 0 && !selectedCompanyId) {
      const firstCompany = allData.companies[0];
      handleCompanySelect({
        value: `${firstCompany.id}-${firstCompany.type}`,
        label: `${firstCompany.name} - ${firstCompany.type}`,
      });
    }
  }, [allData.companies, selectedCompanyId, handleCompanySelect]);

  // Re-filter whenever data or filters change.
  useEffect(() => {
    filterTodos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allData.todos,
    selectedCompanyId,
    selectedCompanyType,
    selectedTag,
    selectedAssignee,
    selectedFollower,
    selectedReporter,
    selectedStatus,
    selectedRecurring,
    dueDateFilter,
    createDateFilter,
    sortBy,
  ]);

  // ---- stats -------------------------------------------------------------
  const pendingTodos = filteredStatTodos.filter((t) => t.stage === 0).length;
  const inProgressTodos = filteredStatTodos.filter((t) => t.stage === 1).length;
  const onHoldTodos = filteredStatTodos.filter((t) => t.stage === 2).length;
  const completedTodos = filteredStatTodos.filter((t) => t.stage === 3).length;

  const statToday = new Date();
  statToday.setHours(0, 0, 0, 0);
  const overdueTodos = filteredStatTodos.filter((todo) => {
    const dueDate = new Date(todo.due_date);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate < statToday && todo.stage !== 3;
  }).length;

  // ---- filtering ---------------------------------------------------------
  const applyCommonFilters = (input: Todo[]): Todo[] => {
    let filtered = input;

    if (selectedCompanyId) {
      filtered = filtered.filter(
        (todo) =>
          todo.company_id == selectedCompanyId &&
          todo.company_type == selectedCompanyType,
      );
    }

    if (selectedAssignee !== "All") {
      filtered = filtered.filter((todo) =>
        todo.assigned_members.some((member) => member.id === selectedAssignee),
      );
    }

    if (selectedFollower !== "All") {
      filtered = filtered.filter((todo) =>
        todo.team_leads.some((member) => member.id === selectedFollower),
      );
    }

    if (selectedReporter !== "All Reporters") {
      filtered = filtered.filter(
        (todo) => todo.reporter?.uid.toString() === selectedReporter,
      );
    }

    if (selectedTag !== "All Tags") {
      filtered = filtered.filter((todo) => todo.tags.includes(selectedTag));
    }

    if (selectedRecurring !== "All") {
      filtered = filtered.filter((todo) =>
        selectedRecurring === "Recurring"
          ? todo.recurring_date !== null
          : todo.recurring_date === null,
      );
    }

    if (dueDateFilter && dueDateFilter.start && dueDateFilter.end) {
      const startDate = moment(dueDateFilter.start).startOf("day");
      const endDate = moment(dueDateFilter.end).endOf("day");
      filtered = filtered.filter((todo) => {
        if (!todo.due_date) return false;
        const todoDate = moment(todo.due_date);
        return todoDate >= startDate && todoDate <= endDate;
      });
    }

    if (createDateFilter && createDateFilter.start && createDateFilter.end) {
      const startDate = moment(createDateFilter.start).startOf("day");
      const endDate = moment(createDateFilter.end).endOf("day");
      filtered = filtered.filter((todo) => {
        if (!todo.createdAt) return false;
        const todoDate = moment(todo.createdAt);
        return todoDate >= startDate && todoDate <= endDate;
      });
    }

    return [...filtered].sort((a, b) => {
      if (
        sortBy === "Last 7 Days" ||
        sortBy === "Last 1 Month" ||
        sortBy === "Last 1 Year"
      ) {
        return (
          new Date(b.createdAt || "").getTime() -
          new Date(a.createdAt || "").getTime()
        );
      }
      return 0;
    });
  };

  const filterTodos = () => {
    // Main list also applies the status filter.
    let filtered = allData.todos;
    if (selectedCompanyId) {
      filtered = filtered.filter(
        (todo) =>
          todo.company_id == selectedCompanyId &&
          todo.company_type == selectedCompanyType,
      );
    }

    if (selectedStatus !== "All") {
      if (selectedStatus === "overdue") {
        filtered = filtered.filter((todo) => {
          const dueDate = todo.due_date ? new Date(todo.due_date) : null;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (dueDate) dueDate.setHours(0, 0, 0, 0);
          return dueDate && today > dueDate && todo.stage !== 3;
        });
      } else {
        filtered = filtered.filter(
          (todo) => todo.stage === parseInt(selectedStatus),
        );
      }
    }

    // Apply the remaining (status-independent) filters + sort.
    const mainResult = applyCommonFilters(filtered);
    setFilteredTodos(mainResult);

    // Stat counters ignore the status filter so every counter stays visible.
    if (selectedStatus === "All") {
      setFilteredStatTodos(mainResult);
    } else {
      setFilteredStatTodos(applyCommonFilters(allData.todos));
    }
  };

  // ---- search ------------------------------------------------------------
  const filteredTodosSearch = allData.todos.filter((todo) => {
    const q = searchText.toLowerCase();
    const idMatch = todo.id?.toString().toLowerCase().includes(q);
    const titleMatch = todo.title?.toLowerCase().includes(q);
    const priorityLabel =
      PRIORITY_OPTIONS.find((p) => parseInt(p.value) === todo.priority)
        ?.label?.toLowerCase() || "";
    const priorityMatch = priorityLabel.includes(q);
    const reporterMatch = todo.reporter?.name?.toLowerCase().includes(q);
    const followersMatch = todo.team_leads?.some((m) =>
      m.name?.toLowerCase().includes(q),
    );
    const membersMatch = todo.assigned_members?.some((m) =>
      m.name?.toLowerCase().includes(q),
    );
    return (
      idMatch ||
      titleMatch ||
      priorityMatch ||
      reporterMatch ||
      followersMatch ||
      membersMatch
    );
  });

  useEffect(() => {
    setFilteredTodos(filteredTodosSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  // ---- assignee/follower options ----------------------------------------
  const getFilteredUsers = () => {
    if (!selectedCompanyId || !selectedCompanyType) return allData.users;
    return allData.users.filter((u) => {
      const userCompanyId =
        typeof (u as any).CompanyId === "string"
          ? parseInt((u as any).CompanyId)
          : (u as any).CompanyId;
      return (
        userCompanyId == parseInt(selectedCompanyId) &&
        (u as any).CompanyType == selectedCompanyType
      );
    });
  };

  const employeeOptions = useMemo(() => {
    return (
      allData?.users?.map((u: any) => ({
        label: getFullName(u.Salutation, u.Fname, u.Lname) || u?.Name,
        value: u.UID?.toString(),
        key: u.id,
        image_url: u?.Image || "assets/img/users/user.png",
        uid: u.UID,
      })) || []
    );
  }, [allData?.users]);

  // ---- handlers ----------------------------------------------------------
  const handleAddTodo = () => {
    setEditModalTodo(null);
    setViewModalTodo(null);
  };

  const handleEditTodo = (todo: Todo) => {
    setEditModalTodo(todo);
    setViewModalTodo(null);
  };

  const handleViewTodo = async (todo: Todo) => {
    setViewModalTodo(todo);
    setEditModalTodo(null);
    try {
      await updateReadStatus(todo.id || "");
    } catch {
      /* read-status failures are non-fatal */
    }
  };

  const handleModalSave = async () => {
    await refreshTodos();
    setEditModalTodo(null);
  };

  const handleTagFilterChange = (tag: string) =>
    setTempFilters((prev) => ({ ...prev, selectedTag: tag }));
  const handleAssigneeFilterChange = (assignee: string) =>
    setTempFilters((prev) => ({
      ...prev,
      selectedAssignee: assignee === "Assignee" ? "All" : assignee,
    }));
  const handleFollowerFilterChange = (assignee: string) =>
    setTempFilters((prev) => ({
      ...prev,
      selectedFollower: assignee === "Assignee" ? "All" : assignee,
    }));
  const handleStatusFilterChange = (status: string) =>
    setTempFilters((prev) => ({ ...prev, selectedStatus: status }));
  const handleRecurringFilterChange = (recurring: string) =>
    setTempFilters((prev) => ({ ...prev, selectedRecurring: recurring }));
  const handleReporterFilterChange = (reporter: string) =>
    setTempFilters((prev) => ({ ...prev, selectedReporter: reporter }));

  const applyFilters = () => {
    setSelectedTag(tempFilters.selectedTag);
    setSelectedAssignee(tempFilters.selectedAssignee);
    setselectedFollower(tempFilters.selectedFollower);
    setSelectedReporter(tempFilters.selectedReporter);
    setSelectedStatus(tempFilters.selectedStatus);
    setSelectedRecurring(tempFilters.selectedRecurring);
    setDueDateFilter(tempFilters.dueDateFilter);
    setcreateDateFilter(tempFilters.createDateFilter);
    setSortBy(tempFilters.sortBy);
    setIsFilterPanelOpen(false);
  };

  const resetFilters = () => {
    setTempFilters({ ...LIST_INITIAL_FILTERS });
    setSelectedTag(LIST_INITIAL_FILTERS.selectedTag);
    setSelectedAssignee(LIST_INITIAL_FILTERS.selectedAssignee);
    setselectedFollower(LIST_INITIAL_FILTERS.selectedFollower);
    setSelectedReporter(LIST_INITIAL_FILTERS.selectedReporter);
    setSelectedStatus(LIST_INITIAL_FILTERS.selectedStatus);
    setSelectedRecurring(LIST_INITIAL_FILTERS.selectedRecurring);
    setDueDateFilter(LIST_INITIAL_FILTERS.dueDateFilter);
    setcreateDateFilter(LIST_INITIAL_FILTERS.createDateFilter);
    setSortBy(LIST_INITIAL_FILTERS.sortBy);
    setSelectKey((prev) => prev + 1);
  };

  const handleSelectStatus = (status: string) => {
    setSelectedStatus(status);
    setTempFilters((prev) => ({ ...prev, selectedStatus: status }));
  };

  // ---- export ------------------------------------------------------------
  const handleExportTodos = async ({
    startDate,
    endDate,
    stage,
    employeeUID,
  }: ExportPayload) => {
    if (!selectedCompany) {
      notify.error("Please select a company first");
      return;
    }
    if (startDate.isAfter(endDate)) {
      notify.error("Start date cannot be after end date");
      return;
    }

    notify.success("Download Started");

    const stageMap: any = { pending: 0, inprogress: 1, onhold: 2, completed: 3 };
    const selectedStage = stage !== "all" ? stageMap?.[stage] : null;

    try {
      const blob: any = await api.post(
        "todoService/exportTodoData",
        {
          signature,
          companyId: selectedCompanyId,
          companyType: selectedCompanyType,
          startDate: startDate.format("YYYY-MM-DD"),
          endDate: endDate.format("YYYY-MM-DD"),
          department_id: null,
          employeeId: employeeUID || null,
          stage: selectedStage,
        },
        { responseType: "blob" },
      );

      const url = window.URL.createObjectURL(new Blob([blob.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Todos_List_${new Date().toISOString().split("T")[0]}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      notify.error("Download Failed");
    }
  };

  // ---- table columns -----------------------------------------------------
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      sorter: (a: Todo, b: Todo) => Number(a.id) - Number(b.id),
      render: (_: any, record: Todo) => (
        <div>
          <strong>{record.id}</strong>
        </div>
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      sorter: (a: Todo, b: Todo) => a.title.length - b.title.length,
      render: (title: string, record: Todo) => (
        <div className="d-flex align-items-center">
          <span className="me-1 rating-select d-flex align-items-center">
            {record.isRead ? (
              <i className="ti ti-checks text-success" />
            ) : (
              <i className="ti ti-check" />
            )}
          </span>
          <button
            className="btn btn-link fw-medium text-dark p-0 m-0 text-start"
            title={capitalizeFirstLetter(title)}
            onClick={() => handleViewTodo(record)}
            data-bs-toggle="modal"
            data-bs-target="#view_todo"
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "200px",
              textDecoration: "none",
            }}
          >
            {formatTitle(title, 15)}
          </button>
        </div>
      ),
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: (a: Todo, b: Todo) =>
        new Date(a.createdAt ?? 0).getTime() -
        new Date(b.createdAt ?? 0).getTime(),
      render: (value?: string) =>
        value ? moment(value).format("DD MMMM YYYY") : "N/A",
    },
    {
      title: "Due Date",
      dataIndex: "due_date",
      key: "due_date",
      sorter: (a: Todo, b: Todo) =>
        new Date(a.due_date || "").getTime() -
        new Date(b.due_date || "").getTime(),
      render: (value: string, record: Todo) => {
        const today = new Date();
        const dueDate = new Date(value);
        const isOverdue =
          dueDate &&
          today.setHours(0, 0, 0, 0) > dueDate.setHours(0, 0, 0, 0) &&
          record.stage !== 3;
        const overdueDays = isOverdue
          ? Math.max(1, moment().diff(moment(dueDate), "days"))
          : 0;

        return (
          <div className="text-center">
            {value ? moment(value).format("DD MMMM YYYY") : "N/A"}
            {isOverdue && (
              <div className="text-danger mt-1" style={{ fontSize: "0.85em" }}>
                {overdueDays} Day{overdueDays > 1 ? "s" : ""} Overdue
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      sorter: (a: Todo, b: Todo) => a.priority - b.priority,
      render: (value: number) => (
        <span
          className={`badge ${
            value === 0
              ? "badge-danger"
              : value === 1
                ? "badge-warning"
                : "badge-success"
          }`}
        >
          {PRIORITY_OPTIONS.find((opt) => parseInt(opt.value) === value)
            ?.label || "N/A"}
        </span>
      ),
    },
    {
      title: "Stage",
      dataIndex: "stage",
      sorter: (a: any, b: any) => a.stage - b.stage,
      render: (stage: number, task: any) => {
        const menu = (
          <Menu>
            {STAGE_OPTIONS.map((option) => (
              <Menu.Item
                key={option.value}
                onClick={() => handleStageUpdate(task.id, option.value)}
              >
                {option.label}
              </Menu.Item>
            ))}
          </Menu>
        );

        const currentLabel =
          STAGE_OPTIONS.find((opt) => parseInt(opt.value) === stage)?.label ||
          "N/A";

        return (
          <Dropdown overlay={menu} trigger={["click"]} placement="bottomLeft">
            <Button
              className={`btn btn-sm dropdown-toggle ${getStageButtonClass(
                stage,
              )}`}
              style={{
                minWidth: 100,
                textAlign: "center",
                whiteSpace: "nowrap",
              }}
            >
              {currentLabel}
            </Button>
          </Dropdown>
        );
      },
    },
    {
      title: "Progress",
      dataIndex: "stage",
      key: "progress",
      sorter: (a: Todo, b: Todo) => getProgress(a.stage) - getProgress(b.stage),
      render: (stage: number) => {
        const progress = getProgress(stage);
        const radius = 20;
        const circumference = 2 * Math.PI * radius;
        const offset = circumference - (progress / 100) * circumference;
        const getColorClass = () => {
          if (progress === 100) return "text-success";
          if (progress >= 50) return "text-purple";
          if (progress >= 25) return "text-warning";
          return "text-danger";
        };
        return (
          <div className="d-flex align-items-center">
            <div
              className="position-relative"
              style={{ width: 50, height: 50 }}
            >
              <svg className="circular-progress" width="50" height="50">
                <circle
                  className="progress-bg"
                  cx="25"
                  cy="25"
                  r={radius}
                  stroke="#f3f3f3"
                  strokeWidth="4"
                  fill="none"
                />
                <circle
                  className={`progress-fill ${getColorClass()}`}
                  cx="25"
                  cy="25"
                  r={radius}
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={offset}
                  transform="rotate(-90 25 25)"
                />
              </svg>
              <div className="position-absolute top-50 start-50 translate-middle">
                <small className="fw-bold">{progress}%</small>
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: "Assigned By",
      dataIndex: "reporter",
      sorter: (a: any, b: any) => {
        const nameA = a.reporter?.name?.toLowerCase() || "";
        const nameB = b.reporter?.name?.toLowerCase() || "";
        return nameA.localeCompare(nameB);
      },
      render: (member: Record<string, any>) => (
        <div className="avatar-list-stacked">
          {member && (
            <span
              className="avatar avatar-rounded bg-success text-white d-inline-flex align-items-center justify-content-center border border-white"
              title={member.name}
              style={{
                width: "40px",
                height: "40px",
                fontSize: "14px",
                overflow: "hidden",
              }}
            >
              {getEmployeeImageUrl(allData.users, member.uid) ? (
                <img
                  src={getEmployeeImageUrl(allData.users, member.uid)}
                  alt={member.name}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <span>{member.name?.charAt(0).toUpperCase() || "?"}</span>
              )}
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Followers",
      dataIndex: "team_leads",
      sorter: (a: any, b: any) => {
        const nameA = a.team_leads?.[0]?.name?.toLowerCase() || "";
        const nameB = b.team_leads?.[0]?.name?.toLowerCase() || "";
        return nameA.localeCompare(nameB);
      },
      render: (members: Array<Record<string, any>>) => (
        <div className="avatar-list-stacked">
          {members.slice(0, 3).map((member, index) => (
            <span
              key={index}
              className="avatar avatar-rounded bg-success text-white d-inline-flex align-items-center justify-content-center border border-white"
              title={member.name}
              style={{
                width: "40px",
                height: "40px",
                fontSize: "14px",
                overflow: "hidden",
              }}
            >
              {getEmployeeImageUrl(allData.users, member.id) ? (
                <img
                  src={getEmployeeImageUrl(allData.users, member.id)}
                  alt={member.name}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <span>{member.name?.charAt(0).toUpperCase() || "?"}</span>
              )}
            </span>
          ))}
          {members.length > 3 && (
            <span
              className="avatar avatar-rounded bg-light text-dark border border-white d-inline-flex align-items-center justify-content-center"
              style={{ width: "40px", height: "40px", fontSize: "14px" }}
            >
              +{members.length - 3}
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Members",
      dataIndex: "assigned_members",
      sorter: (a: any, b: any) => {
        const nameA = a.assigned_members?.[0]?.name?.toLowerCase() || "";
        const nameB = b.assigned_members?.[0]?.name?.toLowerCase() || "";
        return nameA.localeCompare(nameB);
      },
      render: (members: Array<Record<string, any>>) => (
        <div className="avatar-list-stacked">
          {members.slice(0, 3).map((member, index) => (
            <span
              key={index}
              className="avatar avatar-rounded bg-primary text-white d-inline-flex align-items-center justify-content-center border border-white"
              title={member.name}
              style={{
                width: "40px",
                height: "40px",
                fontSize: "14px",
                overflow: "hidden",
              }}
            >
              {getEmployeeImageUrl(allData.users, member.id) ? (
                <img
                  src={getEmployeeImageUrl(allData.users, member.id)}
                  alt={member.name}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <span>{member.name?.charAt(0).toUpperCase() || "?"}</span>
              )}
            </span>
          ))}
          {members.length > 3 && (
            <span
              className="avatar avatar-rounded bg-light text-dark border border-white d-inline-flex align-items-center justify-content-center"
              style={{ width: "40px", height: "40px", fontSize: "14px" }}
            >
              +{members.length - 3}
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, todo: Todo) => {
        const isAdmin = user?.userDetails?.role_char === "Admin";
        const isReporter = user?.userDetails?.uid === todo.reporter?.uid;
        return (
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item key="view">
                  <button
                    className="dropdown-item"
                    onClick={() => handleViewTodo(todo)}
                    data-bs-toggle="modal"
                    data-bs-target="#view_todo"
                  >
                    <i className="ti ti-eye me-2" /> View
                  </button>
                </Menu.Item>
                <Menu.Item key="edit">
                  <button
                    className="dropdown-item"
                    onClick={() => handleEditTodo(todo)}
                    data-bs-toggle="modal"
                    data-bs-target="#add_todo"
                  >
                    <i className="ti ti-edit me-2" /> Edit
                  </button>
                </Menu.Item>
                {(isAdmin || isReporter) && (
                  <Menu.Item key="transfer">
                    <button
                      className="dropdown-item"
                      onClick={() => setTransferModalTodo(todo)}
                    >
                      <i className="ti ti-transfer me-2" /> Transfer
                    </button>
                  </Menu.Item>
                )}
                <Menu.Item key="history">
                  <button
                    className="dropdown-item"
                    onClick={() => setLogsModalTodo(todo)}
                  >
                    <i className="ti ti-history me-2" /> History
                  </button>
                </Menu.Item>
                {(isAdmin || isReporter) && (
                  <Menu.Item key="delete">
                    <button
                      className="dropdown-item text-danger"
                      onClick={() => setDeleteTodoId(todo.id || "")}
                      data-bs-toggle="modal"
                      data-bs-target="#delete_modal"
                    >
                      <i className="ti ti-trash me-2" /> Delete
                    </button>
                  </Menu.Item>
                )}
              </Menu>
            }
            trigger={["click"]}
          >
            <button className="btn btn-sm btn-light">
              <i className="ti ti-dots-vertical" />
            </button>
          </Dropdown>
        );
      },
    },
  ];

  // ---- options for the filter panel -------------------------------------
  const reporterImageOptions = reporterOptions.map((u: any) => ({
    label: u.label,
    value: u.value,
    imageUrl: getEmployeeImageUrl(allData.users, u.value),
  }));
  const memberImageOptions = getFilteredUsers().map((u: any) => ({
    label: u.Name,
    value: u.UID,
    imageUrl: getEmployeeImageUrl(allData.users, u.UID),
  }));

  return (
    <>
      {isLoading && <Loader />}
      <div className="page-wrapper">
        <div className="content">
          <TodoToolbar
            view={view}
            companies={allData.companies}
            selectedCompanyId={selectedCompanyId}
            selectedCompanyType={selectedCompanyType}
            onCompanySelect={handleCompanySelect}
            onAddTodo={handleAddTodo}
            onOpenFilters={() => setIsFilterPanelOpen(true)}
            activeFiltersCount={activeFiltersCount}
            onExport={() => setShowExportModal(true)}
            exportDisabled={isLoading}
          />

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="col-sm-12">
                <div className="d-flex flex-wrap align-items-center justify-content-between">
                  <div className="d-flex flex-wrap align-items-center gap-3">
                    <TodoStatsBar
                      stats={{
                        pending: pendingTodos,
                        overdue: overdueTodos,
                        inProgress: inProgressTodos,
                        onHold: onHoldTodos,
                        completed: completedTodos,
                      }}
                      onSelectStatus={handleSelectStatus}
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      className="form-control form-control-sm p-2"
                      placeholder="Search"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      style={{ minWidth: "200px" }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              <div className="custom-datatable-filter table-responsive">
                <Table
                  key={filteredTodos.length}
                  columns={columns}
                  dataSource={filteredTodos}
                  Selection={false}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2025 © ZYVO.</p>
          <p>
            Designed &amp; Developed By{" "}
            <a href="#" className="text-primary">
              ZYVO
            </a>
          </p>
        </div>
      </div>

      <TodoFilterPanel
        isOpen={isFilterPanelOpen}
        onClose={() => setIsFilterPanelOpen(false)}
        activeFiltersCount={activeFiltersCount}
        tempFilters={tempFilters}
        setTempFilters={setTempFilters}
        tagOptions={TAG_OPTIONS}
        statusOptions={STATUS_OPTIONS}
        recurringOptions={RECURRING_OPTIONS}
        reporterOptions={reporterImageOptions}
        followerOptions={memberImageOptions}
        assigneeOptions={memberImageOptions}
        onTagChange={handleTagFilterChange}
        onReporterChange={handleReporterFilterChange}
        onFollowerChange={handleFollowerFilterChange}
        onAssigneeChange={handleAssigneeFilterChange}
        onStatusChange={handleStatusFilterChange}
        onRecurringChange={handleRecurringFilterChange}
        onApply={applyFilters}
        onReset={resetFilters}
        selectKey={selectKey}
      />

      <TodoModal
        company={selectedCompany}
        todo={editModalTodo}
        viewTodo={viewModalTodo}
        onSave={handleModalSave}
      />

      <DeleteModal
        onDelete={() => handleDeleteTodo(deleteTodoId)}
        modalId="delete_modal"
        title="Delete Item"
        message="Are you sure you want to delete this item? This action cannot be undone."
      />

      <ExportTodoModal
        show={showExportModal}
        onClose={() => setShowExportModal(false)}
        companyName={selectedCompany?.name}
        employeeOptions={employeeOptions}
        onExport={handleExportTodos}
      />

      <TransferTaskModal
        todo={transferModalTodo}
        signature={signature}
        onClose={() => setTransferModalTodo(null)}
        onTransferred={handleTaskTransferred}
      />

      <TaskHistoryModal
        todo={logsModalTodo}
        signature={signature}
        onClose={() => setLogsModalTodo(null)}
      />
    </>
  );
};

export default TodoListView;
